/**
 * Axios mutator used by orval-generated hooks.
 *
 * Stripped-down version of mDenials-portal's customInstance:
 *  - Demo has no auth, so no Bearer-token interceptor and no 401 redirect.
 *  - Keeps paramsSerializer (FastAPI repeats array params: foo=1&foo=2).
 *  - Keeps global error→toast and large-body limits for multipart uploads.
 */

import Axios, {
    type AxiosError,
    type AxiosRequestConfig,
    type CancelTokenSource,
} from 'axios';
import { toast } from '@/components/ui/sonner';

const AXIOS_INSTANCE = Axios.create({
    baseURL: typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000',
    // Send the demo access-gate cookie with every request (it's HttpOnly,
    // so JS never reads it — the browser attaches it because of this flag).
    withCredentials: true,
    // Support large file uploads (PDFs can be 50MB+)
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    paramsSerializer: {
        serialize: (params: Record<string, unknown>) => {
            const searchParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((item) => searchParams.append(key, String(item)));
                } else if (value !== undefined && value !== null) {
                    searchParams.set(key, String(value));
                }
            });
            return searchParams.toString();
        },
    },
});

AXIOS_INSTANCE.interceptors.response.use(
    (response) => response,
    (error: AxiosError<{ error?: string; detail?: string }>) => {
        // React Query cancels in-flight queries on unmount / strict-mode
        // double-mount. Those bubble up here as Cancel errors — don't
        // toast them, they aren't failures.
        if (Axios.isCancel(error) || error.code === 'ERR_CANCELED') {
            return Promise.reject(error);
        }
        // The demo access-gate cookie expired (or was cleared) mid-session.
        // Tell the gate to drop back to the login screen instead of toasting
        // a raw 401 on every in-flight request.
        if (error.response?.status === 401) {
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
            return Promise.reject(error);
        }
        // The sessionId pinned in the URL (for sharing) points at a session
        // the server no longer has — typically after a backend restart wiped
        // in-memory state. Don't toast a scary 404; signal the app to drop the
        // stale pointer and clear the querystring so a refresh starts clean.
        if (
            error.response?.status === 404 &&
            (error.config?.method ?? '').toLowerCase() === 'get' &&
            /^\/api\/sessions\/[^/]+$/.test(error.config?.url ?? '')
        ) {
            window.dispatchEvent(new CustomEvent('session:expired'));
            return Promise.reject(error);
        }
        const data = error.response?.data;
        const msg =
            (typeof data?.error === 'string' && data.error) ||
            (typeof data?.detail === 'string' && data.detail) ||
            error.message ||
            'Request failed';
        toast.error(msg);
        return Promise.reject(error);
    },
);

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
    const source: CancelTokenSource = Axios.CancelToken.source();
    const promise = AXIOS_INSTANCE({ ...config, cancelToken: source.token }).then(
        ({ data }) => data,
    );
    // @ts-expect-error attaching cancel for react-query cancellation
    promise.cancel = () => source.cancel('Query was cancelled by React Query');
    return promise;
};

export default customInstance;

export type ErrorType<Error> = AxiosError<Error>;
